import { DocumentSnapshot } from 'firebase/firestore';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { compressAccurately } from 'image-conversion';
import { message } from 'ant-design-vue';
import { AxiosError } from 'axios';
import { amplitudeInstance } from '@/analytics';
import config from '@/config';
import { i18n } from '@/lang';

import {
  PackageNames,
  PackageTypes,
  FormatPackages,
  ApiPricingPackage,
} from '@/@types/bankroll';

import { ServerError } from '@/@types/base';

import { ApiChatMessage, ChatMessage } from '@/@types/user';
import { ApiCareType, ApiCareTypeFormated, FlowerCare } from '@/@types/plant';

dayjs.extend(utc);

export async function compressImage(file: File): Promise<File> {
  const expectedSize = 1.8; // in MiB
  const fileSize = +(file.size / (1024 * 1024)).toFixed(1); // in MiB

  if (fileSize <= expectedSize) {
    return file;
  }

  const resBlob = await compressAccurately(file, 1500); // 1500 Kb

  return new File([resBlob], file.name);
}

export function goToPricing() {
  const amplitudeEvents = {
    Home: 'home_page',
    MyPlants: 'my_plants_page',
    Onboarding: 'onboarding',
    SearchResults: 'search_result_page',
    Plant: 'plant_description_page',
  };

  const route = useRoute();
  const routeName = route.name as keyof typeof amplitudeEvents;

  if (amplitudeEvents[routeName]) {
    amplitudeInstance.logEvent('Pricing_page_shown', {
      referrer: amplitudeEvents[routeName],
    });
  }

  window.open(`https://${config.shopHost}/payment/membership`, '_blank');
}

export function formatFirebaseMsgs(docs: DocumentSnapshot<ApiChatMessage>[]) {
  const msgArray: ChatMessage[] = [];

  docs.forEach((doc) => {
    const msg = doc.data();

    if (!msg) return null;

    if (msg.type === 1) {
      msgArray.push({
        author: msg.sender_type === 4 ? 'me' : 'bot',
        type: 'text',
        data: { text: msg.content },
      });
    }

    if (msg.type === 2) {
      msgArray.push({
        author: msg.sender_type === 4 ? 'me' : 'bot',
        type: 'file',
        data: {
          file: {
            url: `https://lilyplant.nyc3.cdn.digitaloceanspaces.com/public/chat/${msg.content}`,
            name: msg.content,
          },
        },
      });
    }
  });

  return msgArray;
}

type CareObj = Record<ApiCareType['id'], ApiCareTypeFormated>;

export function convertCareTypesListToObj(careTypes: ApiCareType[]) {
  return careTypes.reduce((acc, careType) => {
    if (careType.id === 0) return acc;

    const name = careType.name.split(' ');

    const formatCare: ApiCareTypeFormated = {
      ...careType,
      title: name[0],
      icon: name[0].toLowerCase(),
      formatDate: i18n.global.t('text_not_set'),
    };

    return { ...acc, [careType.id]: formatCare };
  }, {} as CareObj);
}

export function formatCareRemiders({
  careTypes,
  plantCares,
}: {
  careTypes: ApiCareType[];
  plantCares: FlowerCare[];
}): ApiCareTypeFormated[] {
  const careTypesObj = convertCareTypesListToObj(careTypes);

  plantCares.forEach((plantCare) => {
    setCareTypesData(careTypesObj, plantCare);
  });

  return Object.values(careTypesObj);
}

export function setCareTypesData(caresObj: CareObj, plantCare: FlowerCare) {
  if (plantCare.type.id === 0) return;

  const careId = plantCare.type.id;

  caresObj[careId].period = plantCare.period;
  caresObj[careId].time = plantCare.time;
  caresObj[careId].timeZone = plantCare.timeZone;

  if (plantCare.period !== 0) {
    const utcDate = dayjs(plantCare.time * 1000).utcOffset(plantCare.timeZone);

    caresObj[careId].formatDate = utcDate.format('DD MMM, hh:mm A');
  }
}

export function animateAddToMyPlants(el: HTMLElement) {
  if (window.innerWidth < 992) return;

  const destination = document.getElementById('my-plants');

  if (!destination) return;

  const destinationRect = destination.getBoundingClientRect();
  const departureRect = el.getBoundingClientRect();
  const shadowEl = document.createElement('div');

  shadowEl.className = 'shadow-el';

  shadowEl.style.width = `${departureRect.width}px`;
  shadowEl.style.height = `${departureRect.height}px`;
  shadowEl.style.transform = `translate(${departureRect.left}px, ${departureRect.top}px) scale(0.9)`;

  document.body.appendChild(shadowEl);

  shadowEl.addEventListener('transitionend', () => shadowEl.remove(), false);

  setTimeout(() => {
    shadowEl.style.transform = `translate(${destinationRect.left}px, ${destinationRect.top}px) scale(0.4)`;
    shadowEl.style.opacity = '0';
  }, 0);
}

export function formatDate(dateObj: Date) {
  const formatter = new Intl.DateTimeFormat('ru');
  const [date, month, year] = formatter.format(dateObj).split('.');

  return `${year}-${month}-${date}`;
}

export function formatPackages(packages: ApiPricingPackage[]) {
  const re = /(_1y|_99y|_1m)/g;

  const packObj: FormatPackages<PackageNames> = {
    '1m': {
      name: '1m',
      type: 'month',
      title: i18n.global.t('text_month'),
      description: i18n.global.t('text_monthly_purchase'),
      info: i18n.global.t('text_auto_renewable'),
      hook: '',
      price: {
        withoutDiscount: {
          base: 15.99,
          perDay: 0.53,
          discount: '',
        },
        firstDiscount: {
          base: 12.99,
          perDay: 0.43,
          discount: '-20%',
        },
        secondDiscount: {
          base: 7.99,
          perDay: 0.26,
          discount: '-50%',
        },
        baseDescription: i18n.global.t('text_per_month'),
        perDayDescription: i18n.global.t('text_per_day'),
      },
    },
    '1y': {
      name: '1y',
      type: 'annual',
      title: i18n.global.t('text_annual'),
      description: i18n.global.t('text_yearly_purchase'),
      hook: i18n.global.t('text_trial_purchase'),
      info: i18n.global.t('text_auto_renewable'),
      price: {
        withoutDiscount: {
          base: 47.99,
          perDay: 0.13,
          discount: '',
        },
        firstDiscount: {
          base: 19.99,
          perDay: 0.1,
          discount: '-20%',
        },
        secondDiscount: {
          base: 23.99,
          perDay: 0.06,
          discount: '-50%',
        },
        baseDescription: i18n.global.t('text_per_year'),
        perDayDescription: i18n.global.t('text_per_day'),
      },
    },
    '99y': {
      name: '99y',
      type: 'lifetime',
      title: i18n.global.t('text_lifetime'),
      description: i18n.global.t('text_lifetime_access'),
      hook: '',
      info: i18n.global.t('text_one_time_purchase'),
      price: {
        withoutDiscount: {
          base: 71.99,
          perDay: 0.07,
          discount: '',
        },
        firstDiscount: {
          base: 59.99,
          perDay: 0.005,
          discount: '20%',
        },
        secondDiscount: {
          base: 35.99,
          perDay: 0.03,
          discount: '50%',
        },
        baseDescription: i18n.global.t('text_one_time_purchase'),
        perDayDescription: i18n.global.t('text_per_month'),
      },
    },
  };

  packages.forEach((pack) => {
    // packageKey - lilyplant:trial_1y
    const [, fullName] = pack.packageKey.split(':');

    // packName - trial_1y
    const keyIdx = fullName.search(re);

    // trial
    const packName = (
      keyIdx === -1 ? 'default' : fullName.slice(0, keyIdx)
    ) as PackageTypes;

    // 1y
    const packType = (
      keyIdx === -1 ? fullName : fullName.slice(keyIdx + 1)
    ) as PackageNames;

    packObj[packType][packName] = pack;
  });

  // console.log(packObj);

  return packObj;
}

export function detectIos() {
  const prop = document.documentElement.style.getPropertyValue(
    '-webkit-touch-callout'
  );

  return prop !== '';
}

export function processApiErrorMessages(err: AxiosError<ServerError>) {
  if (!err.response) return;

  const messages = err.response.data.errors.flatMap(
    (errItem) => errItem.messages
  );

  messages.forEach((msg, idx) => {
    idx === 0 ? message.error(msg) : setTimeout(() => message.error(msg), 1000);
  });
}

const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export const validateEmail = (email: string) => {
  return String(email).toLowerCase().match(emailRegExp);
};

export const constants = {
  GATEWAY_ID: 7,
  OFFER_HASH: process.env.VUE_APP_OFFER_HASH ?? '',
  AUTH_HASH: 'auth_user_hash',
  AUTH_KEY: 'auth_key',
  AUTH_UUID: 'auth_uuid',
  AUTH_EMAIL: 'auth_email',
  ORDER_SESSION: 'sessionId',
  ORDER_ID: 'orderId',
  ORDER_ITEM: 'itemNo',
};
